/**
 * Thin fetch wrapper around `GET /v1/collections` / `GET /v1/collections/:id`.
 * Unauthenticated on purpose — public catalog browsing (architecture §5) —
 * mirrors `modules/entitlements/api.ts`'s local-fetch convention rather than
 * the authenticated `request()` in `infrastructure/lib/api.ts`.
 */
import { API_BASE } from '@/src/infrastructure/lib/api';
import type { Collection, CollectionListQuery, PaginatedCollections } from './types';

export class CatalogNotFoundError extends Error {
  constructor() {
    super('Product not found or no longer available');
    this.name = 'CatalogNotFoundError';
  }
}

async function publicFetch<T>(url: string): Promise<T> {
  const res = await fetch(url);
  if (res.status === 404) {
    throw new CatalogNotFoundError();
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.message ?? `HTTP ${res.status}`);
  }
  const body = await res.json();
  return (body?.data !== undefined ? body.data : body) as T;
}

export function fetchCollections(query: CollectionListQuery = {}): Promise<PaginatedCollections> {
  const params = new URLSearchParams();
  if (query.page) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  if (query.occasionType) params.set('occasionType', query.occasionType);
  if (query.sort) params.set('sort', query.sort);
  const qs = params.toString();
  return publicFetch<PaginatedCollections>(`${API_BASE}/collections${qs ? `?${qs}` : ''}`);
}

export function fetchCollection(id: string): Promise<Collection> {
  return publicFetch<Collection>(`${API_BASE}/collections/${encodeURIComponent(id)}`);
}
